import React, { useState, useEffect, useCallback } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Space,
  Typography,
  Modal,
  Form,
  message,
  Drawer,
} from 'antd';
import {
  PlusOutlined,
  BarChartOutlined,
  BookOutlined,
  SaveOutlined,
  UploadOutlined,
  DownloadOutlined,
} from '@ant-design/icons';

// Import shared components and utilities
import LibraryTable from '../components/shared/LibraryTable';
import FilterPanel from '../components/shared/FilterPanel';
import StatsCard from '../components/shared/StatsCard';
import { Exercise, ExerciseFilters, MuscleGroup, EquipmentReference } from '../types';
import { filterExercises, debounce, getUniqueValues, getFilterOptions } from '../utils/filterUtils';
import { 
  EXERCISE_TYPES, 
  SYMMETRY_MODES, 
  DIFFICULTY_LEVELS, 
  RATING_LEVELS, 
  DANGER_LEVELS, 
  REP_RANGES,
  SEARCH_DEBOUNCE_MS 
} from '../constants';

// Import feature-specific components (to be created)
import ExerciseForm from './components/ExerciseForm';
import ExerciseDetailsModal from './components/ExerciseDetailsModal';
import ExerciseStatsDrawer from './components/ExerciseStatsDrawer';
import { getExerciseTableColumns } from './utils/exerciseTableConfig';
import { mockExercises, mockMuscleGroups, mockEquipment } from './data/mockData';
import { apiService } from '../../../services/api';

const { Title } = Typography;

const ExerciseLibrary: React.FC = () => {
  // State management
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [muscleGroups, setMuscleGroups] = useState<MuscleGroup[]>([]);
  const [equipment, setEquipment] = useState<EquipmentReference[]>([]);
  const [filteredExercises, setFilteredExercises] = useState<Exercise[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  
  // Modal states
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [isStatsDrawerVisible, setIsStatsDrawerVisible] = useState(false);
  const [editingExercise, setEditingExercise] = useState<Exercise | null>(null);
  const [selectedExerciseForDetails, setSelectedExerciseForDetails] = useState<Exercise | null>(null);
  
  // Filter states
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [filters, setFilters] = useState<ExerciseFilters>({
    searchText: '',
    types: [],
    difficultyRanges: [],
    muscleGroups: [],
    equipmentTypes: [],
    symmetryModes: [],
    ratingRanges: [],
    dangerRanges: [],
    repRanges: [],
  });

  const [form] = Form.useForm();

  // Load data from API
  useEffect(() => {
    const loadData = async () => {
      try {
        setInitialLoading(true);
        
        // Load exercises with proper pagination
        const [exercisesResponse, muscleGroupsResponse, equipmentResponse] = await Promise.all([
          apiService.getExercises({ limit: 100 }), // Load first 100 exercises
          apiService.getMuscleGroups(),
          apiService.getEquipment({ limit: 100 }) // Load first 100 equipment items
        ]);

        if (exercisesResponse.success && exercisesResponse.data) {
          // Handle different response formats from backend
          const exerciseData = Array.isArray(exercisesResponse.data) 
            ? exercisesResponse.data 
            : exercisesResponse.data.exercises || [];
          setExercises(exerciseData);
        } else {
          console.warn('Failed to load exercises, using mock data');
          setExercises(mockExercises);
        }

        if (muscleGroupsResponse.success && muscleGroupsResponse.data) {
          setMuscleGroups(muscleGroupsResponse.data);
        } else {
          console.warn('Failed to load muscle groups, using mock data');
          setMuscleGroups(mockMuscleGroups);
        }

        if (equipmentResponse.success && equipmentResponse.data) {
          // Handle equipment response format
          const equipmentData = Array.isArray(equipmentResponse.data)
            ? equipmentResponse.data
            : equipmentResponse.data.data || [];
          // Convert to EquipmentReference format for compatibility
          const equipmentRefs = equipmentData.map(eq => ({ id: eq.id, name: eq.name }));
          setEquipment(equipmentRefs);
        } else {
          console.warn('Failed to load equipment, using mock data');
          setEquipment(mockEquipment);
        }

      } catch (error) {
        console.error('Error loading library data:', error);
        message.error('Failed to load library data. Using cached data.');
        // Fallback to mock data on error
        setExercises(mockExercises);
        setMuscleGroups(mockMuscleGroups);
        setEquipment(mockEquipment);
      } finally {
        setInitialLoading(false);
      }
    };

    loadData();
  }, []);

  // Apply filters with debouncing
  const debouncedFilter = useCallback(
    debounce(() => {
      const filtered = filterExercises(exercises, filters);
      setFilteredExercises(filtered);
    }, SEARCH_DEBOUNCE_MS),
    [exercises, filters]
  );

  useEffect(() => {
    debouncedFilter();
  }, [debouncedFilter]);

  // Filter handlers
  const handleFilterChange = (key: keyof ExerciseFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleClearAllFilters = () => {
    setFilters({
      searchText: '',
      types: [],
      difficultyRanges: [],
      muscleGroups: [],
      equipmentTypes: [],
      symmetryModes: [],
      ratingRanges: [],
      dangerRanges: [],
      repRanges: [],
    });
  };

  const getTotalFiltersApplied = () => {
    let count = 0;
    if (filters.searchText) count++;
    count += filters.types.length;
    count += filters.difficultyRanges.length;
    count += filters.muscleGroups.length;
    count += filters.equipmentTypes.length;
    count += filters.symmetryModes.length;
    count += filters.ratingRanges.length;
    count += filters.dangerRanges.length;
    count += filters.repRanges.length;
    return count;
  };

  // Table handlers
  const handleAdd = () => {
    setEditingExercise(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record: Exercise) => {
    setEditingExercise(record);
    form.setFieldsValue(record);
    setIsModalVisible(true);
  };

  const handleDelete = async (record: Exercise) => {
    try {
      setLoading(true);
      const response = await apiService.deleteExercise(record.id);
      
      if (response.success) {
        setExercises(prev => prev.filter(item => item.id !== record.id));
        message.success('Exercise deleted successfully');
      } else {
        message.error('Failed to delete exercise');
      }
    } catch (error) {
      console.error('Error deleting exercise:', error);
      message.error('Failed to delete exercise');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (record: Exercise) => {
    setSelectedExerciseForDetails(record);
    setIsDetailModalVisible(true);
  };

  const handleSave = async (values: any) => {
    try {
      setLoading(true);
      
      // Transform form data to match backend validation schema
      const transformedValues = {
        ...values,
        // Convert muscleActivations object to muscleGroups array
        muscleGroups: values.muscleActivations 
          ? Object.keys(values.muscleActivations)
              .filter(id => values.muscleActivations[id] > 0)
              .map(id => {
                const muscle = muscleGroups.find(m => m.id === id);
                return muscle?.name || id;
              })
              .filter(name => name && name.trim()) // Remove empty names
          : [],
        // Convert equipment object to equipment array (include if quantity > 0)  
        equipment: values.equipment
          ? Object.keys(values.equipment)
              .filter(id => values.equipment[id] && values.equipment[id] > 0)
              .map(id => {
                const equipmentItem = equipment.find(e => e.id === id);
                return equipmentItem?.name || id;
              })
              .filter(name => name && name.trim()) // Remove empty names
          : [],
        // Ensure difficulty is within range 1-5 (backend expects 1-5, form uses 1-10)
        difficulty: values.difficulty ? Math.min(Math.max(Math.round(values.difficulty / 2), 1), 5) : 1,
      };
      
      // Validation: Ensure required fields
      if (!transformedValues.name || transformedValues.name.trim().length < 2) {
        message.error('Exercise name must be at least 2 characters');
        return;
      }
      
      if (transformedValues.muscleGroups.length === 0) {
        message.error('Please select at least one muscle group');
        return;
      }
      
      // Equipment is optional for bodyweight exercises
      // if (transformedValues.equipment.length === 0) {
      //   message.error('Please select at least one piece of equipment');
      //   return;
      // }
      
      // Remove the original muscleActivations field since we converted it to muscleGroups
      delete transformedValues.muscleActivations;
      
      console.log('Transformed exercise data:', transformedValues);
      
      if (editingExercise) {
        // Update existing exercise
        const response = await apiService.updateExercise(editingExercise.id, transformedValues);
        
        if (response.success && response.data) {
          setExercises(prev => prev.map(item => 
            item.id === editingExercise.id ? response.data! : item
          ));
          message.success('Exercise updated successfully');
        } else {
          message.error('Failed to update exercise');
          return;
        }
      } else {
        // Add new exercise
        const response = await apiService.createExercise(transformedValues);
        
        if (response.success && response.data) {
          setExercises(prev => [...prev, response.data!]);
          message.success('Exercise added successfully');
        } else {
          message.error('Failed to create exercise');
          return;
        }
      }
      
      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Error saving exercise:', error);
      message.error('Failed to save exercise');
    } finally {
      setLoading(false);
    }
  };

  // Get filter options
  const getFilterGroups = () => [
    {
      key: 'types',
      label: 'Exercise Type',
      type: 'multiSelect' as const,
      options: getFilterOptions(Object.values(EXERCISE_TYPES)),
      value: filters.types,
      onChange: (value: string[]) => handleFilterChange('types', value),
      placeholder: 'Select types...',
      allowClear: true,
      showCount: true,
    },
    {
      key: 'symmetryModes',
      label: 'Symmetry Mode',
      type: 'multiSelect' as const,
      options: getFilterOptions(Object.values(SYMMETRY_MODES)),
      value: filters.symmetryModes,
      onChange: (value: string[]) => handleFilterChange('symmetryModes', value),
      placeholder: 'Select modes...',
      allowClear: true,
    },
    {
      key: 'difficultyRanges',
      label: 'Difficulty Level',
      type: 'multiSelect' as const,
      options: getFilterOptions(Object.values(DIFFICULTY_LEVELS).map(d => d.label)),
      value: filters.difficultyRanges,
      onChange: (value: string[]) => handleFilterChange('difficultyRanges', value),
      placeholder: 'Select difficulty...',
      allowClear: true,
    },
    {
      key: 'ratingRanges',
      label: 'Rating',
      type: 'multiSelect' as const,
      options: getFilterOptions(Object.values(RATING_LEVELS).map(r => r.label)),
      value: filters.ratingRanges,
      onChange: (value: string[]) => handleFilterChange('ratingRanges', value),
      placeholder: 'Select rating...',
      allowClear: true,
    },
    {
      key: 'dangerRanges',
      label: 'Danger Level',
      type: 'multiSelect' as const,
      options: getFilterOptions(Object.values(DANGER_LEVELS).map(d => d.label)),
      value: filters.dangerRanges,
      onChange: (value: string[]) => handleFilterChange('dangerRanges', value),
      placeholder: 'Select danger level...',
      allowClear: true,
    },
    {
      key: 'repRanges',
      label: 'Rep Range',
      type: 'multiSelect' as const,
      options: getFilterOptions(Object.values(REP_RANGES).map(r => r.label)),
      value: filters.repRanges,
      onChange: (value: string[]) => handleFilterChange('repRanges', value),
      placeholder: 'Select rep range...',
      allowClear: true,
    },
    {
      key: 'muscleGroups',
      label: 'Muscle Groups',
      type: 'multiSelect' as const,
      options: getFilterOptions(muscleGroups.map(mg => mg.name)),
      value: filters.muscleGroups,
      onChange: (value: string[]) => handleFilterChange('muscleGroups', value),
      placeholder: 'Select muscle groups...',
      allowClear: true,
      showCount: true,
    },
    {
      key: 'equipmentTypes',
      label: 'Equipment',
      type: 'multiSelect' as const,
      options: getFilterOptions(equipment.map(eq => eq.name)),
      value: filters.equipmentTypes,
      onChange: (value: string[]) => handleFilterChange('equipmentTypes', value),
      placeholder: 'Select equipment...',
      allowClear: true,
      showCount: true,
    },
  ];

  return (
    <div>
      <Row gutter={[24, 24]}>
        {/* Header */}
        <Col span={24}>
          <Card>
            <Row justify="space-between" align="middle">
              <Col>
                <Title level={2} style={{ margin: 0 }}>
                  <BookOutlined /> Exercise Library
                </Title>
              </Col>
              <Col>
                <Space>
                  <Button 
                    icon={<BarChartOutlined />} 
                    onClick={() => setIsStatsDrawerVisible(true)}
                  >
                    Statistics
                  </Button>
                  <Button icon={<UploadOutlined />}>Import</Button>
                  <Button icon={<DownloadOutlined />}>Export</Button>
                  <Button 
                    type="primary" 
                    icon={<PlusOutlined />} 
                    onClick={handleAdd}
                    loading={loading}
                  >
                    Add Exercise
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Filters */}
        <Col span={24}>
          <FilterPanel
            searchValue={filters.searchText}
            onSearchChange={(value) => handleFilterChange('searchText', value)}
            filterGroups={getFilterGroups()}
            onClearAll={handleClearAllFilters}
            expanded={filtersExpanded}
            onExpandedChange={setFiltersExpanded}
            totalFiltersApplied={getTotalFiltersApplied()}
          />
        </Col>

        {/* Table */}
        <Col span={24}>
          <Card>
            <LibraryTable
              data={filteredExercises}
              columns={getExerciseTableColumns()}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onView={handleView}
              selectedRowKeys={selectedRowKeys}
              onSelectionChange={(keys, rows) => setSelectedRowKeys(keys)}
              loading={initialLoading || loading}
              pagination={{
                total: filteredExercises.length,
                showTotal: (total, range) => 
                  `Showing ${range[0]}-${range[1]} of ${total} exercises`,
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Exercise Form Modal */}
      <Modal
        title={editingExercise ? 'Edit Exercise' : 'Add New Exercise'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={800}
        destroyOnHidden
      >
        <ExerciseForm
          form={form}
          exercise={editingExercise}
          muscleGroups={muscleGroups}
          equipment={equipment}
          onSave={handleSave}
          onCancel={() => setIsModalVisible(false)}
        />
      </Modal>

      {/* Exercise Details Modal */}
      <ExerciseDetailsModal
        visible={isDetailModalVisible}
        exercise={selectedExerciseForDetails}
        muscleGroups={muscleGroups}
        equipment={equipment}
        onClose={() => setIsDetailModalVisible(false)}
      />

      {/* Statistics Drawer */}
      <ExerciseStatsDrawer
        visible={isStatsDrawerVisible}
        exercises={exercises}
        muscleGroups={muscleGroups}
        equipment={equipment}
        onClose={() => setIsStatsDrawerVisible(false)}
      />
    </div>
  );
};

export default ExerciseLibrary; 